// ── AR Flow — NOVA INTERFACE, Etapa 2a: lista do Atendimento Presencial ──────
// Só visual. Recebe os clientes JÁ FILTRADOS pela tela atual (período, busca,
// consultor, permissões) e, ao clicar, chama a mesma função de abrir o card
// (handleSelectClient), que abre o ClientModal com a conversa do WhatsApp.
// Não grava nada, não muda etapa, não chama API.

import { useState } from "react";
import TextSendStatus, { useTextSendPriority } from "../TextSendStatus";
import ClientOriginLabels from "../ClientOriginLabels";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";

// Etapas do Presencial, na ordem do caminho combinado com a Ana.
const STAGE_LABEL = {
  waiting: "Em acompanhamento",
  msg_sent: "Mensagem enviada",
  final: "Em negociação",
  future: "Prospecção futura",
  semInteresse: "Sem interesse",
  closed: "Fechou venda",
  posVenda: "Pós-venda",
  nextSale: "Próxima venda",
};
const OPEN_STAGES = ["waiting", "msg_sent", "final"];
const SOLD_STAGES = ["closed", "posVenda", "nextSale"];

const FILTERS = [
  { id: "abertos", label: "Em aberto", match: c => OPEN_STAGES.includes(c.stage) },
  { id: "waiting", label: "Em acompanhamento", match: c => c.stage === "waiting" },
  { id: "msg_sent", label: "Mensagem enviada", match: c => c.stage === "msg_sent" },
  { id: "final", label: "Em negociação", match: c => c.stage === "final" },
  { id: "future", label: "Prospecção futura", match: c => c.stage === "future" },
  { id: "semInteresse", label: "Sem interesse", match: c => c.stage === "semInteresse" },
  { id: "vendidos", label: "Fechou venda", match: c => SOLD_STAGES.includes(c.stage) },
  { id: "todos", label: "Todos", match: () => true },
];

const brl = v => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

function dayOnly(value) {
  if (!value) return null;
  // O campo de data do CRM salva YYYY-MM-DD. new Date("YYYY-MM-DD") usa UTC
  // e pode mostrar o dia anterior no fuso de São Paulo.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const local = new Date(year, month - 1, day);
    if (local.getFullYear() !== year || local.getMonth() !== month - 1 || local.getDate() !== day) return null;
    return local.getTime();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function followUpInfo(value) {
  const day = dayOnly(value);
  if (day == null) return null;
  const today = dayOnly(Date.now());
  const label = new Date(day).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  if (day < today) return { label: `Atrasado · ${label}`, tone: "late", day };
  if (day === today) return { label: "Hoje", tone: "today", day };
  return { label, tone: "future", day };
}

export default function PresencialList({ theme, clients, isAwaiting, consultantName, showConsultant, onOpen, selectedId = null }) {
  const th = theme;
  const [filterId, setFilterId] = useState("abertos");
  const line = th.nuLine || th.sandDark;

  const openClients = clients.filter(c => OPEN_STAGES.includes(c.stage));
  const openTotal = openClients.reduce((s, c) => s + (Number(c.value) || 0), 0);

  const filter = FILTERS.find(f => f.id === filterId) || FILTERS[0];
  const rows = clients
    .filter(filter.match)
    .map(c => ({ c, awaiting: Boolean(isAwaiting(c)), fu: followUpInfo(c.followUp) }))
    .sort((a, b) => {
      if (a.awaiting !== b.awaiting) return a.awaiting ? -1 : 1;
      const fa = a.fu ? a.fu.day : Infinity;
      const fb = b.fu ? b.fu.day : Infinity;
      if (fa !== fb) return fa - fb;
      return (new Date(b.c.createdAt || 0).getTime() || 0) - (new Date(a.c.createdAt || 0).getTime() || 0);
    });
  const prioritizedRows = useTextSendPriority(rows, ({ c }) => ({ storeId:c.storeId, phone:c.phone }));

  return (
    <section aria-label="Clientes do atendimento presencial" style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", padding: "4px 0 14px", borderBottom: `1px solid ${line}` }}>
        <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 500, color: th.text }}>{brl(openTotal)}</div>
        <div style={{ fontSize: 14, color: th.muted }}>
          em orçamentos abertos · {openClients.length} cliente{openClients.length === 1 ? "" : "s"}
          {showConsultant ? " · todos os consultores" : ""}
        </div>
      </div>

      <div role="tablist" aria-label="Filtrar por etapa" style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "12px 0" }}>
        {FILTERS.map(f => {
          const active = f.id === filterId;
          const count = clients.filter(f.match).length;
          return (
            <button key={f.id} role="tab" aria-selected={active} onClick={() => setFilterId(f.id)} style={{
              border: `1px solid ${active ? th.accent : line}`, background: active ? th.accent : "transparent",
              color: active ? "#FFFFFF" : th.text, borderRadius: 4, padding: "6px 10px", cursor: "pointer",
              fontSize: 13, fontWeight: active ? 700 : 500, fontFamily: "inherit",
            }}>
              {f.label} <span style={{ fontFamily: MONO, opacity: 0.8 }}>{count}</span>
            </button>
          );
        })}
      </div>

      <div style={{ borderTop: `1px solid ${line}` }}>
        {rows.length === 0 && (
          <div style={{ padding: "28px 4px", color: th.muted, fontSize: 14 }}>
            Nenhum cliente nesta etapa. Use "+ Novo Cliente" para cadastrar um atendimento.
          </div>
        )}
        {prioritizedRows.map(({ c, awaiting, fu }) => (
          <button key={c.id} onClick={() => onOpen(c)} aria-current={c.id === selectedId ? "true" : undefined} style={{
            width: "100%", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "center",
            textAlign: "left", background: c.id === selectedId ? (th.sandDark || "#F4EBDD") : awaiting ? "rgba(180,35,24,0.04)" : "transparent", border: "none",
            boxShadow: c.id === selectedId ? `inset 3px 0 0 ${th.accent}` : "none",
            borderBottom: `1px solid ${line}`, padding: "12px 4px", cursor: "pointer", fontFamily: "inherit", color: th.text,
          }}>
            <span style={{ minWidth: 0 }}>
              <TextSendStatus storeId={c.storeId} phone={c.phone} />
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {awaiting && <span aria-label="Aguardando sua resposta" style={{ width: 8, height: 8, borderRadius: "50%", background: "#B42318", flexShrink: 0 }} />}
                <span style={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 700 }}>{c.name || "Sem nome"}</span>
                <span style={{ fontSize: 12, color: th.muted }}>{STAGE_LABEL[c.stage] || c.stage}</span>
              </span>
              <span style={{ display: "block", fontSize: 13, color: th.muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[c.product, showConsultant ? consultantName(c.consultantId) : null, awaiting ? "Aguardando sua resposta" : null].filter(Boolean).join(" · ") || "\u00A0"}
              </span>
              <ClientOriginLabels client={c} />
            </span>
            <span style={{ textAlign: "right" }}>
              <span style={{ display: "block", fontFamily: MONO, fontSize: 15 }}>{Number(c.value) > 0 ? brl(c.value) : "—"}</span>
              {fu && (
                <span style={{ display: "block", fontSize: 12, marginTop: 3, fontWeight: fu.tone === "future" ? 500 : 700,
                  color: fu.tone === "late" ? "#B42318" : fu.tone === "today" ? "#B45309" : th.muted }}>
                  {fu.label}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}